import React, { useState } from 'react';
import { Box } from '@mantine/core';

import CompaniesTab from './companies/CompaniesTab';
import UnprocessedValuationUpdatesTab from 'src/secret-menu/bulk-valuations/UnprocessedValuationUpdatesTab';
import { Navigate, useParams } from 'react-router';
import TemplatesListPage from 'src/company/templates/components/TemplatesListPage';
import PageWithSidebar from 'src/UI/page-nav/PageWithSidebar';
import DeprecatedTemplateTab from 'src/secret-menu/templates/DeprecatedTemplateTab';
import FeatureFlagsTab from 'src/secret-menu/feature-flags/FeatureFlagsTab';
import FeatureFlagsPage from 'src/secret-menu/feature-flags/FeatureFlagsPage';
import CompanyUsersPage from 'src/secret-menu/company-users/CompanyUsersPage';
import EditorPlaygroundTab from './editor-playground/EditorPlaygroundTab';
import CompanyClientsPage from 'src/secret-menu/company-clients/CompanyClientsPage';
import FilesTab from 'src/secret-menu/files/FilesTab';
import GeneratedDocumentsTab from 'src/secret-menu/generated-documents/GeneratedDocumentsTab';
import BackgroundProcessesTab from 'src/secret-menu/background-processes/BackgroundProcessesTab';
import TenantSettingsTab from 'src/secret-menu/tenant-settings/TenantSettingsTab';
import SharepointTab from 'src/secret-menu/sharepoint/SharepointTab';
import InfoTab from 'src/secret-menu/info/InfoTab';
import SandboxTab from 'src/secret-menu/sandbox/SandboxTab';
import TopicTreeTab from 'src/secret-menu/topic-tree/TopicTreeTab';
import LlmConfigTab from './llm-config/LlmConfigTab';
import LlmDispatcherTab from './llm-dispatcher/LlmDispatcherTab';
import CustomSkillsTab from './custom-skills/CustomSkillsTab';
import ProductKnowledgeTab from './product-knowledge/ProductKnowledgeTab';
import ExportAssetsTab from 'src/secret-menu/export-assets/ExportAssetsTab';
import AutoBrandTab from 'src/secret-menu/auto-brand/AutoBrandTab';
import { useIsAfternoon } from 'src/common/useIsAfternoonHook';
import BotMigrationTab from 'src/secret-menu/bot-migration/BotMigrationTab';
import CalendarHealthTab from 'src/secret-menu/calendar-health/CalendarHealthTab';
import HealthCheckTab from 'src/secret-menu/health-check/HealthCheckTab';
import ConversationDebuggerTab from 'src/secret-menu/conversation-debugger/ConversationDebuggerTab';
import TranscriptTesterTab from 'src/secret-menu/transcript-tester/TranscriptTesterTab';
import OpenBankingTab from 'src/secret-menu/open-banking/OpenBankingTab';
import DeriveFrnTestTab from 'src/secret-menu/derive-frn-test/DeriveFrnTestTab';
import AutoBrandFromUrlTab from 'src/secret-menu/auto-brand-from-url/AutoBrandFromUrlTab';
import JoinRequestsTab from 'src/secret-menu/join-requests/JoinRequestsTab';
import JoinRequestDrawer from 'src/secret-menu/join-requests/JoinRequestDrawer';
import TenantApiKeysTab from 'src/secret-menu/tenant-api-keys/TenantApiKeysTab';
import MicrosoftEmailTab from 'src/secret-menu/microsoft-email/MicrosoftEmailTab';
import { useFlags } from 'src/feature-flags/FlagsHook';

const SecretMenuTabs = {
  companies: 'companies',
  companyUsers: 'company-users',
  companyClients: 'company-clients',
  templates: 'templates',
  oldTemplates: 'old-templates',
  bulkValuations: 'bulk-valuations',
  files: 'files',
  generatedDocuments: 'generated-documents',
  backgroundProcesses: 'background-processes',
  featureFlags: 'feature-flags',
  editorPlayground: 'editor-playground',
  inlineEdit: 'inline-edit',
  tenantSettings: 'tenant-settings',
  sharepoint: 'sharepoint',
  info: 'info',
  sandbox: 'sandbox',
  topicTree: 'topic-tree',
  llmConfig: 'llm-config',
  llmDispatcher: 'llm-dispatcher',
  exportAssets: 'export-assets',
  autoBrand: 'auto-brand',
  productKnowledge: 'product-knowledge',
  customSkills: 'custom-skills',
  botMigration: 'bot-migration',
  calendarHealth: 'calendar-health',
  healthCheck: 'health-check',
  conversationDebugger: 'conversation-debugger',
  transcriptTester: 'transcript-tester',
  openBanking: 'open-banking',
  deriveFrnTest: 'derive-frn-test',
  autoBrandFromUrl: 'auto-brand-from-url',
  joinRequests: 'join-requests',
  ldFlagStatuses: 'ld-flag-statuses',
  tenantApiKeys: 'tenant-api-keys',
  microsoftEmail: 'microsoft-email',
} as const;

export default function SecretMenuPage() {
  const { tab } = useParams<{ tab: string }>();
  const flags = useFlags();
  const { isAfternoon } = useIsAfternoon();
  const [selectedJoinRequestId, setSelectedJoinRequestId] = useState<string | null>(null);

  const SECRET_MENU_LINKS = [
    {
      label: 'Companies',
      href: '/secret-menu/companies',
    },
    {
      label: 'Company users',
      href: '/secret-menu/company-users',
    },
    {
      label: 'Company clients',
      href: '/secret-menu/company-clients',
    },
    {
      label: 'Templates',
      href: '/secret-menu/templates',
    },
    {
      label: 'Templates (old)',
      href: '/secret-menu/old-templates',
    },
    {
      label: 'Bulk valuations',
      href: '/secret-menu/bulk-valuations',
    },
    {
      label: 'Files',
      href: '/secret-menu/files',
    },
    {
      label: 'Generated documents',
      href: '/secret-menu/generated-documents',
    },
    {
      label: 'Background processes',
      href: '/secret-menu/background-processes',
    },
    {
      label: 'Feature flags',
      href: '/secret-menu/feature-flags',
    },
    {
      label: 'LD flag statuses',
      href: '/secret-menu/ld-flag-statuses',
    },
    {
      label: 'Editor playground',
      href: '/secret-menu/editor-playground',
    },
    {
      label: 'Tenant settings',
      href: '/secret-menu/tenant-settings',
    },
    {
      label: 'Sharepoint (Beta)',
      href: '/secret-menu/sharepoint',
    },
    {
      label: 'Info',
      href: '/secret-menu/info',
    },
    {
      label: 'Calendar Health',
      href: '/secret-menu/calendar-health',
    },
    {
      label: 'Health Check',
      href: '/secret-menu/health-check',
    },
    {
      label: 'Sandbox',
      href: '/secret-menu/sandbox',
    },
    {
      label: 'Topic Tree',
      href: '/secret-menu/topic-tree',
    },
    {
      label: 'LLM Config',
      href: '/secret-menu/llm-config',
    },
    ...(flags.flagEnabledLlmDispatcherAdmin
      ? [
          {
            label: 'LLM Dispatcher',
            href: '/secret-menu/llm-dispatcher',
          },
        ]
      : []),
    {
      label: 'Export Branding',
      href: '/secret-menu/export-assets',
    },
    ...(isAfternoon
      ? [
          {
            label: 'Auto Brand',
            href: '/secret-menu/auto-brand',
          },
        ]
      : []),
    {
      label: 'Product Knowledge',
      href: '/secret-menu/product-knowledge',
    },
    {
      label: 'Custom skills',
      href: '/secret-menu/custom-skills',
    },
    ...(flags.flagEnabledBotMigration
      ? [
          {
            label: 'Bot Migration',
            href: '/secret-menu/bot-migration',
          },
        ]
      : []),
    {
      label: 'Conversation Debugger',
      href: '/secret-menu/conversation-debugger',
    },
    ...(isAfternoon
      ? [
          {
            label: 'Transcript tester',
            href: '/secret-menu/transcript-tester',
          },
        ]
      : []),
    {
      label: 'Open banking',
      href: '/secret-menu/open-banking',
    },
    {
      label: 'FRN derivation test',
      href: '/secret-menu/derive-frn-test',
    },
    {
      label: 'Auto brand from URL test',
      href: '/secret-menu/auto-brand-from-url',
    },
    {
      label: 'Join requests',
      href: '/secret-menu/join-requests',
    },
    {
      label: 'Tenant API keys',
      href: '/secret-menu/tenant-api-keys',
    },
    ...(flags.flagEnabledMicrosoftEmailSend
      ? [
          {
            label: 'Microsoft email',
            href: '/secret-menu/microsoft-email',
          },
        ]
      : []),
  ];

  if (!tab) {
    return <Navigate to="/secret-menu/companies" replace />;
  }

  const renderContent = () => {
    switch (tab) {
      case SecretMenuTabs.companies:
        return <CompaniesTab />;
      case SecretMenuTabs.companyUsers:
        return <CompanyUsersPage />;
      case SecretMenuTabs.companyClients:
        return <CompanyClientsPage />;
      case SecretMenuTabs.templates:
        return <TemplatesListPage />;
      case SecretMenuTabs.oldTemplates:
        return <DeprecatedTemplateTab />;
      case SecretMenuTabs.bulkValuations:
        return <UnprocessedValuationUpdatesTab />;
      case SecretMenuTabs.files:
        return <FilesTab />;
      case SecretMenuTabs.generatedDocuments:
        return <GeneratedDocumentsTab />;
      case SecretMenuTabs.backgroundProcesses:
        return <BackgroundProcessesTab />;
      case SecretMenuTabs.featureFlags:
        return <FeatureFlagsTab />;
      case SecretMenuTabs.ldFlagStatuses:
        return <FeatureFlagsPage />;
      case SecretMenuTabs.editorPlayground:
        return <EditorPlaygroundTab />;
      case SecretMenuTabs.tenantSettings:
        return <TenantSettingsTab />;
      case SecretMenuTabs.sharepoint:
        return <SharepointTab />;
      case SecretMenuTabs.info:
        return <InfoTab />;
      case SecretMenuTabs.calendarHealth:
        return <CalendarHealthTab />;
      case SecretMenuTabs.healthCheck:
        return <HealthCheckTab />;
      case SecretMenuTabs.sandbox:
        return <SandboxTab />;
      case SecretMenuTabs.topicTree:
        return <TopicTreeTab />;
      case SecretMenuTabs.llmConfig:
        return <LlmConfigTab />;
      case SecretMenuTabs.llmDispatcher:
        return flags.flagEnabledLlmDispatcherAdmin ? (
          <LlmDispatcherTab />
        ) : (
          <Navigate to="/secret-menu/companies" replace />
        );
      case SecretMenuTabs.exportAssets:
        return <ExportAssetsTab />;
      case SecretMenuTabs.autoBrand:
        return isAfternoon ? <AutoBrandTab /> : <Navigate to="/secret-menu/companies" replace />;
      case SecretMenuTabs.productKnowledge:
        return <ProductKnowledgeTab />;
      case SecretMenuTabs.customSkills:
        return <CustomSkillsTab />;
      case SecretMenuTabs.botMigration:
        return flags.flagEnabledBotMigration ? (
          <BotMigrationTab />
        ) : (
          <Navigate to="/secret-menu/companies" replace />
        );
      case SecretMenuTabs.conversationDebugger:
        return <ConversationDebuggerTab />;
      case SecretMenuTabs.transcriptTester:
        return isAfternoon ? (
          <TranscriptTesterTab />
        ) : (
          <Navigate to="/secret-menu/companies" replace />
        );
      case SecretMenuTabs.openBanking:
        return <OpenBankingTab />;
      case SecretMenuTabs.deriveFrnTest:
        return <DeriveFrnTestTab />;
      case SecretMenuTabs.autoBrandFromUrl:
        return <AutoBrandFromUrlTab />;
      case SecretMenuTabs.joinRequests:
        return (
          <JoinRequestsTab
            onSelectRequest={setSelectedJoinRequestId}
            onRejectRequest={setSelectedJoinRequestId}
          />
        );
      case SecretMenuTabs.tenantApiKeys:
        return <TenantApiKeysTab />;
      case SecretMenuTabs.microsoftEmail:
        return flags.flagEnabledMicrosoftEmailSend ? (
          <MicrosoftEmailTab />
        ) : (
          <Navigate to="/secret-menu/companies" replace />
        );
      default:
        return <Navigate to="/secret-menu/companies" replace />;
    }
  };

  return (
    <Box component="pre">
      <PageWithSidebar links={SECRET_MENU_LINKS} activeUrl={`/company/${tab}`}>
        {renderContent()}
      </PageWithSidebar>
      <JoinRequestDrawer
        requestId={selectedJoinRequestId}
        onClose={() => setSelectedJoinRequestId(null)}
      />
    </Box>
  );
}
